import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { createSingleUser, bulkUploadUsers } from '../../apis/createUser.service';

const INITIAL_FORM_STATE = {
  username: "",
  email: "",
  phoneNo: "",
  firstName: "",
  lastName: "",
  vendor_Id: ""
};


const CreateUser = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResetForm = () => {
    setFormData(INITIAL_FORM_STATE);
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token")?.replaceAll(/"/g, "") || "";
      const result = await createSingleUser(formData, token);

      Swal.fire({
        icon: 'success',
        title: 'User Created',
        text: typeof result === 'object' ? 'User successfully created!' : result,
        confirmButtonColor: '#0b8a0b'
      });
    } catch (err) {
      let errorTitle = 'Creation Failed';
      let errorHtml = '';

      try {
        const parsedError = JSON.parse(err.message);
        errorTitle = parsedError.error || 'Creation Failed';
        if (parsedError.existingEmails && parsedError.existingEmails.length > 0) {
          errorHtml = `
            <style>
              .swal-badge-container::-webkit-scrollbar { display: none; }
            </style>
            <div style="text-align: left; font-size: 14px; color: #333;">
              <p style="font-weight: 600; color: #d9534f; margin-bottom: 12px; font-size: 15px;">
                ${parsedError.message || "Validation Error occurred."}
              </p>
              <div style="margin-bottom: 8px; font-weight: 600; color: #555;">Conflicting Emails:</div>
              <div class="swal-badge-container" style="max-height: 150px; overflow-y: auto; background-color: #fcf8e3; border: 1px solid #faebcc; padding: 10px; border-radius: 6px; display: flex; flex-wrap: wrap; gap: 6px; scrollbar-width: none; -ms-overflow-style: none;">
                ${Array.from(new Set(parsedError.existingEmails)).map(email => `
                  <span style="background-color: #f2dede; color: #a94442; border: 1px solid #ebccd1; padding: 3px 8px; border-radius: 12px; font-size: 12px; font-family: monospace; display: inline-block;">
                    ${email}
                  </span>
                `).join('')}
              </div>
            </div>
          `;
        } else {
          errorHtml = `<div style="text-align: center; color: #d9534f; font-weight: 500;">${parsedError.message || 'An error occurred while creating the user.'}</div>`;
        }
      } catch (e) {
        errorHtml = `<div style="text-align: center; color: #d9534f; font-weight: 500;">${err.message || 'An error occurred while creating the user.'}</div>`;
      }

      Swal.fire({
        icon: 'error',
        title: errorTitle,
        html: errorHtml,
        confirmButtonColor: '#0b8a0b'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid File',
          text: 'Please select a valid CSV file.',
          confirmButtonColor: '#0b8a0b'
        });
        return;
      }
      setCsvFile(file);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      Swal.fire({
        icon: 'warning',
        title: 'No File Selected',
        text: 'Please select a CSV file to upload.',
        confirmButtonColor: '#0b8a0b'
      });
      return;
    }

    setLoading(true);
    setUploadResult(null);
    try {
      const token = localStorage.getItem("token")?.replaceAll(/"/g, "") || "";
      const result = await bulkUploadUsers(csvFile, token);

      if (result && typeof result === 'object' && (result.successCount !== undefined || result.failedCount !== undefined)) {
        Swal.fire({
          icon: result.failedCount > 0 ? 'warning' : 'success',
          title: 'Bulk Upload Completed',
          html: `
            <div style="font-size: 15px; text-align: center;">
              <p style="margin-bottom: 15px; font-weight: 500; color: #333;">${result.message || 'Bulk user upload processed.'}</p>
              <div style="display: flex; justify-content: center; gap: 15px;">
                <span style="background-color: #d1e7dd; color: #0f5132; padding: 6px 12px; border-radius: 4px; font-weight: bold; border: 1px solid #badbcc; font-size: 14px;">
                  Success: ${result.successCount ?? 0}
                </span>
                <span style="background-color: #f8d7da; color: #842029; padding: 6px 12px; border-radius: 4px; font-weight: bold; border: 1px solid #f5c2c7; font-size: 14px;">
                  Failed: ${result.failedCount ?? 0}
                </span>
              </div>
            </div>
          `,
          confirmButtonColor: '#0b8a0b',
          confirmButtonText: 'OK'
        }).then((swalResult) => {
          if (swalResult.isConfirmed || swalResult.isDismissed) {
            setUploadResult(result);
          }
        });
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Bulk Upload Successful',
          text: typeof result === 'object' ? 'Bulk users successfully created!' : result,
          confirmButtonColor: '#0b8a0b'
        });
      }
      setCsvFile(null);
      const fileInput = document.getElementById('csvFileInput');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      let errorTitle = 'Upload Failed';
      let errorHtml = '';

      try {
        const parsedError = JSON.parse(err.message);
        errorTitle = parsedError.error || 'Upload Failed';
        if (parsedError.existingEmails && parsedError.existingEmails.length > 0) {
          errorHtml = `
            <style>
              .swal-badge-container::-webkit-scrollbar { display: none; }
            </style>
            <div style="text-align: left; font-size: 14px; color: #333;">
              <p style="font-weight: 600; color: #d9534f; margin-bottom: 12px; font-size: 15px;">
                ${parsedError.message || "Validation Error occurred."}
              </p>
              <div style="margin-bottom: 8px; font-weight: 600; color: #555;">Conflicting Emails:</div>
              <div class="swal-badge-container" style="max-height: 150px; overflow-y: auto; background-color: #fcf8e3; border: 1px solid #faebcc; padding: 10px; border-radius: 6px; display: flex; flex-wrap: wrap; gap: 6px; scrollbar-width: none; -ms-overflow-style: none;">
                ${Array.from(new Set(parsedError.existingEmails)).map(email => `
                  <span style="background-color: #f2dede; color: #a94442; border: 1px solid #ebccd1; padding: 3px 8px; border-radius: 12px; font-size: 12px; font-family: monospace; display: inline-block;">
                    ${email}
                  </span>
                `).join('')}
              </div>
            </div>
          `;
        } else {
          errorHtml = `<div style="text-align: center; color: #d9534f; font-weight: 500;">${parsedError.message || 'An error occurred during bulk upload.'}</div>`;
        }
      } catch (e) {
        errorHtml = `<div style="text-align: center; color: #d9534f; font-weight: 500;">${err.message || 'An error occurred during bulk upload.'}</div>`;
      }

      Swal.fire({
        icon: 'error',
        title: errorTitle,
        html: errorHtml,
        confirmButtonColor: '#0b8a0b'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-body py-3 px-md-5 w-100" style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <div className="row d-flex justify-content-center">
        <div className="col-lg-10" style={{ paddingTop: '22px' }}>

          <h2 style={{ color: "#0b8a0b", marginBottom: "20px" }}>Create User</h2>

          {/* Tab Navigation */}
          <div className="d-flex border-bottom mb-4" style={{ gap: "10px" }}>
            <button
              type="button"
              className="btn py-2 px-3 border-0 fw-semibold"
              style={{
                color: activeTab === 'single' ? '#0b8a0b' : '#6c757d',
                borderBottom: activeTab === 'single' ? '3px solid #0b8a0b' : '3px solid transparent',
                borderRadius: 0,
                fontSize: '14px',
                background: 'none'
              }}
              onClick={() => {
                setActiveTab('single');
                setUploadResult(null);
              }}
            >
              Single User Form
            </button>
            <button
              type="button"
              className="btn py-2 px-3 border-0 fw-semibold"
              style={{
                color: activeTab === 'bulk' ? '#0b8a0b' : '#6c757d',
                borderBottom: activeTab === 'bulk' ? '3px solid #0b8a0b' : '3px solid transparent',
                borderRadius: 0,
                fontSize: '14px',
                background: 'none'
              }}
              onClick={() => {
                setActiveTab('bulk');
                setUploadResult(null);
              }}
            >
              Upload CSV (Bulk)
            </button>
          </div>

          {activeTab === 'single' ? (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: "1px solid #e8e8e8"
              }}
            >
              <form onSubmit={handleSingleSubmit}>
                <h5 className="mb-4" style={{ color: '#0b8a0b', fontWeight: '700', borderBottom: '1px solid #eee', paddingBottom: '10px', fontSize: '15px' }}>
                  User Information
                </h5>
                <div className="row mb-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>First Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleFormChange}
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Last Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleFormChange}
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Username *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      name="username"
                      value={formData.username}
                      onChange={handleFormChange}
                      placeholder="Enter username"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Email *</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Phone Number</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      name="phoneNo"
                      value={formData.phoneNo}
                      onChange={handleFormChange}
                      placeholder="Enter phone number (optional)"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Vendor ID *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      name="vendor_Id"
                      value={formData.vendor_Id}
                      onChange={handleFormChange}
                      placeholder="Enter Vendor ID"
                      required
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary px-3"
                    style={{ fontSize: "13px", borderRadius: "6px" }}
                    onClick={handleResetForm}
                    disabled={loading}
                  >
                    Clear Form
                  </button>
                  <button
                    type="submit"
                    className="btn btn-sm text-white px-4"
                    style={{ backgroundColor: '#0b8a0b', fontSize: "13px", borderRadius: "6px", fontWeight: "600" }}
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="spinner-border spinner-border-sm text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      "Create User"
                    )}
                  </button>
                </div>
              </form >
            </div >
          ) : (
            <>
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  border: "1px solid #e8e8e8"
                }}
              >
                <div className="row d-flex justify-content-center text-center py-3">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <i className="bi bi-file-earmark-spreadsheet" style={{ fontSize: '3.5rem', color: '#0b8a0b' }}></i>
                    </div>
                    <h5 className="mb-2" style={{ color: '#333', fontWeight: '600' }}>Bulk User Creation</h5>
                    <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
                      Select a CSV file containing user details formatted correctly to upload and perform a bulk creation.
                    </p>

                    <form onSubmit={handleBulkSubmit}>
                      <div className="mb-4 text-start">
                        <label className="form-label" style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>Upload CSV File</label>
                        <input
                          type="file"
                          id="csvFileInput"
                          accept=".csv"
                          onChange={handleFileChange}
                          className="form-control form-control-sm"
                          style={{ fontSize: "13px" }}
                        />
                        {csvFile && (
                          <div className="mt-3 p-2 bg-light border rounded text-start d-flex justify-content-between align-items-center" style={{ fontSize: "13px" }}>
                            <span style={{ fontWeight: '500', color: '#333' }}>
                              <i className="bi bi-file-earmark-check-fill text-success me-2"></i>
                              {csvFile.name}
                            </span>
                            <button
                              type="button"
                              className="btn-close"
                              style={{ padding: '0.25rem' }}
                              onClick={() => {
                                setCsvFile(null);
                                const fileInput = document.getElementById('csvFileInput');
                                if (fileInput) fileInput.value = '';
                              }}
                              aria-label="Remove file"
                            ></button>
                          </div>
                        )}
                      </div>

                      <div className="d-flex justify-content-center">
                        <button
                          type="submit"
                          className="btn text-white"
                          style={{ backgroundColor: '#0b8a0b', padding: '8px 30px', fontSize: "13px", fontWeight: "600", borderRadius: "6px" }}
                          disabled={loading || !csvFile}
                        >
                          {loading ? (
                            <div className="spinner-border spinner-border-sm text-light" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          ) : (
                            "Upload & Create Users"
                          )}
                        </button>
                      </div>
                    </form>

                    <div className="mt-4 p-3 bg-light border rounded text-start" style={{ fontSize: "13px" }}>
                      <h6 className="fw-bold text-dark mb-2">CSV Formatting Guide:</h6>
                      <p className="text-secondary mb-2" style={{ fontSize: "12px" }}>
                        Your CSV file should have a header row and include the following columns:
                      </p>
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-2 text-center" style={{ fontSize: "12px", backgroundColor: "#fff" }}>
                          <thead>
                            <tr style={{ backgroundColor: "#f8f9fa" }}>
                              <th>id</th>
                              <th>username</th>
                              <th>email</th>
                              <th>createdAt</th>
                              <th>phoneNo</th>
                              <th>firstName</th>
                              <th>lastName</th>
                              <th>vendor_Id</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="text-muted"><em>(auto)</em></td>
                              <td>john_sid</td>
                              <td>john.sid@example.com</td>
                              <td className="text-muted"><em>(auto)</em></td>
                              <td>+1234567890</td>
                              <td>John</td>
                              <td>sid</td>
                              <td>vendor_123</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <small className="text-muted block mt-1">
                        * Columns <strong>username</strong>, <strong>email</strong>, <strong>firstName</strong>, <strong>lastName</strong>, and <strong>vendor_Id</strong> are mandatory. <br />
                        * Columns <strong>id</strong> and <strong>createdAt</strong> will be auto-generated if left blank.
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {uploadResult && (
                <div
                  className="mt-4"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "24px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    border: "1px solid #e8e8e8"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                    <h5 style={{ color: '#0b8a0b', margin: 0, fontWeight: '700' }}>Bulk Upload Results</h5>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary px-3"
                      style={{ fontSize: "12px", borderRadius: "6px" }}
                      onClick={() => setUploadResult(null)}
                    >
                      Clear Results
                    </button>
                  </div>

                  <div className="row">
                    {/* Successfully Created Users */}
                    <div className="col-md-6 mb-3">
                      <div className="card h-100 border" style={{ borderColor: '#e8e8e8', borderRadius: '6px' }}>
                        <div className="card-header bg-light d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #e8e8e8' }}>
                          <span className="fw-bold text-success" style={{ fontSize: '13px' }}>Users Created ({uploadResult.users?.length || 0})</span>
                        </div>
                        <div className="card-body p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {uploadResult.users && uploadResult.users.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                                <thead>
                                  <tr>
                                    <th className="border-0 text-muted">Username</th>
                                    <th className="border-0 text-muted">Email</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadResult.users.map((user, idx) => (
                                    <tr key={idx}>
                                      <td className="text-dark fw-semibold py-2 border-0">{user.username || 'N/A'}</td>
                                      <td className="text-secondary py-2 border-0">{user.email || 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center text-muted py-4" style={{ fontSize: '13px' }}>No users created.</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Missing Vendor ID Users */}
                    <div className="col-md-6 mb-3">
                      <div className="card h-100 border" style={{ borderColor: '#e8e8e8', borderRadius: '6px' }}>
                        <div className="card-header bg-light d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #e8e8e8' }}>
                          <span className="fw-bold text-danger" style={{ fontSize: '13px' }}>Missing Vendor ID Users ({uploadResult.missingVendorIdUsers?.length || 0})</span>
                        </div>
                        <div className="card-body p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {uploadResult.missingVendorIdUsers && uploadResult.missingVendorIdUsers.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                                <thead>
                                  <tr>
                                    <th className="border-0 text-muted">Username</th>
                                    <th className="border-0 text-muted">Email</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadResult.missingVendorIdUsers.map((item, idx) => (
                                    <tr key={idx}>
                                      <td className="text-dark fw-semibold py-2 border-0">{item.user?.username || 'N/A'}</td>
                                      <td className="text-secondary py-2 border-0">{item.user?.email || 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center text-muted py-4" style={{ fontSize: '13px' }}>No missing vendor ID users.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div >
      </div >
    </div >
  );
};

export default CreateUser;
